import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import {
  PhoneCall,
  FileText,
  Mail,
  Users,
  BookOpen,
  Video,
  Database,
  MessageCircle,
} from "lucide-react";

const benefits = [
  {
    icon: PhoneCall,
    title: "Weekly 1:1 Calls",
    description:
      "Personal guidance sessions to track your progress and address challenges",
  },
  {
    icon: FileText,
    title: "Resume Revamp",
    description:
      "Professional resume makeover to make you stand out to recruiters",
  },
  {
    icon: Mail,
    title: "Cold Email Templates",
    description: "Proven email scripts that get responses from hiring managers",
  },
  {
    icon: Users,
    title: "Referral Connects",
    description: "Direct connections to employees in your target companies",
  },
  {
    icon: BookOpen,
    title: "Interview Prep",
    description: "Comprehensive mock interviews and feedback sessions",
  },
  {
    icon: Video,
    title: "Live Sessions",
    description:
      "Group sessions covering industry insights and placement strategies",
  },
  {
    icon: Database,
    title: "Resource Vault",
    description:
      "Access to exclusive job boards, study materials and many more resources",
  },
  {
    icon: MessageCircle,
    title: "WhatsApp/Google Meet Access",
    description:
      "Direct communication channel for immediate support and guidance",
  },
];

const PlacementWhatYoullGet = (details) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });

  return (
    <section id="benefits" className="py-24 relative overflow-hidden">
      {/* Background with dots + gradient overlay */}
      <div
        className="absolute inset-0 z-0"
        style={{
          backgroundImage: "radial-gradient(#404040 1px, transparent 1px)",
          backgroundSize: "20px 20px",
        }}
      />
      <div
        className="absolute inset-0 z-0"
        style={{
          background:
            "radial-gradient(ellipse at center, transparent 20%, black 70%)",
        }}
      />

      {/* Content sits on top of background */}
      <div className="container mx-auto px-2 md:px-4 relative z-10" ref={ref}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl lg:text-5xl font-bold mb-6">
            <span className="bg-text-gradient bg-clip-text text-transparent">
              What You'll Get
            </span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Everything you need to land your dream job in just 60 days
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {benefits.map((benefit, index) => {
            const Icon = benefit.icon;

            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{
                  scale: 1.05,
                  transition: { duration: 0.2 },
                }}
                className="group"
              >
                <div className="bg-glass-gradient backdrop-blur-glass border border-primaryTheme/20 rounded-2xl p-6 h-full hover:border-primaryTheme/40 transition-all duration-300 hover:shadow-glow">
                  <div className="flex flex-col items-center text-center space-y-4">
                    <div className="p-3 rounded-xl bg-primaryTheme/10 group-hover:bg-primaryTheme/20 transition-colors">
                      <Icon className="h-8 w-8 text-primaryTheme" />
                    </div>
                    <div className="space-y-2">
                      <h3 className="font-semibold text-lg text-foreground">
                        {benefit.title}
                      </h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {benefit.description}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Call-to-action */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="text-center mt-16"
        >
          <div className="inline-flex items-center px-6 py-3 rounded-full bg-primaryTheme/10 border border-primaryTheme/20">
            <span className="text-primaryTheme font-medium">
              ✨ All this for just{" "}
              {(details?.price || 9999).toLocaleString("en-IN")} — that's less
              than what most people spend on a single course!
            </span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default PlacementWhatYoullGet;
